import React from "react";
import PageHeader from "../../../components/layout/PageHeader";
import {
  LoadPhase,
  SkeletonCard,
  TABLE_BODY_SKELETON,
} from "../../../components/ui";
import { ROUTES } from "../../../routes/paths";

export interface ClientDetailSkeletonProps {
  /**
   * How long the record has been coming. Only `slow` draws the page; while it
   * is merely `waiting` there is the heading and nothing else, so a client
   * that arrives quickly is never preceded by a screenful of blocks.
   */
  phase: LoadPhase;
}

/**
 * The client screen while the record is being fetched.
 *
 * The page as it will stand — the same header, the same tab strip, and the
 * shape of the Plans tab, which is the one the screen opens on — so the client
 * lands into a page that is already the right shape. The breadcrumb stays as
 * words: the trail back to the list is true whether or not the client ever
 * arrives.
 *
 * Shapes rather than contents. What is inside the banner and the table is their
 * own business once they land — drawing the labels and pills and macros twice,
 * once in blocks and once for real, is what makes a loading screen busier than
 * the screen it stands for.
 */
const ClientDetailSkeleton: React.FC<ClientDetailSkeletonProps> = ({
  phase,
}) => (
  <div className="h-full flex flex-col overflow-hidden px-4 py-5 sm:px-6 lg:px-8 3xl:px-12 3xl:py-8">
    <PageHeader
      title={
        <>
          {/* What the heading is read out as, from the moment the request goes
              out. The block beside it is the client's name, which nobody knows
              yet — and which is not drawn at all until the wait is worth
              admitting. */}
          <span className="sr-only">Loading client…</span>
          <SkeletonCard
            phase={phase}
            className="h-5 w-[180px] sm:w-[220px] 3xl:h-6"
          />
        </>
      }
      breadcrumbs={[
        { label: "My clients", to: ROUTES.myClients },
        { label: "Loading…" },
      ]}
    />

    {phase === "slow" && (
      <>
        {/* The pre-consultation strip at the height it keeps, so the tabs
            under it do not jump down the page when the client lands. Rounded
            the way the strip is rather than the way a card is. */}
        <SkeletonCard className="mt-5 h-[86px] flex-shrink-0 rounded-lg" />

        <div className="mt-5 flex-shrink-0 border-b border-line pb-3">
          <SkeletonCard className="h-4 w-[220px]" />
        </div>

        <div className="mt-5 flex-1 min-h-0 flex flex-col gap-4">
          {/* The plan banner at the height it keeps, then the meal plan table
              as one block the size of the card it fills — the same two shapes
              `ClientPlanBanner` and `ClientPlansTab` wait as themselves. */}
          <SkeletonCard className="h-[53px] flex-shrink-0" />
          <SkeletonCard className={`flex-1 ${TABLE_BODY_SKELETON}`} />
        </div>
      </>
    )}
  </div>
);

export default ClientDetailSkeleton;
